import type { TableFilter } from 'types/views';

import {
  HANDLER_SUFFIX,
  MAX_PREFIX,
  MIN_PREFIX,
  RANGE_END_SUFFIX,
  RANGE_START_SUFFIX,
} from './filterGrammar';
import type { FilterValues } from './tableFilterQuery';

type ConstantOption = { value: string | number; description: string };

// A filter is "active" (has an applied value) when any of its grammar-keyed slots is
// set: the bare value, the empty/filled handler, a min/max bound, or a date-range
// start/end bound. Date ranges live under _start/_end — omitting them is why date
// filters did not show as added in the sidebar.
export const isFilterActive = (filter: Pick<TableFilter, 'name'>, filters: FilterValues): boolean =>
  Boolean(
    filters[filter.name] ||
      filters[`${filter.name}${HANDLER_SUFFIX}`] ||
      filters[`${MIN_PREFIX}${filter.name}`] ||
      filters[`${MAX_PREFIX}${filter.name}`] ||
      filters[`${filter.name}${RANGE_START_SUFFIX}`] ||
      filters[`${filter.name}${RANGE_END_SUFFIX}`],
  );

// A stored select value is either an array or a scalar that round-trips through the
// URL as a comma-joined string. Normalise to the individual codes.
const toCodes = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (value === undefined || value === null || value === '') return [];
  return String(value).split(',');
};

// Map coded select values to their human labels via a constant option list, falling
// back to the raw code when an option is missing. Shared by the filter drawer's row
// summary and the toolbar chips (filterChips) so the two surfaces never diverge.
export const labelByConstant = (codes: string[], options: ConstantOption[]): string =>
  codes
    .map(code => options.find(option => String(option.value) === code)?.description ?? code)
    .join(', ');

// The display label for a select filter's stored value, used by the filter drawer's
// row summary. Prefers the filter's named constant (so it is correct on initial load,
// before any interaction populates filtersName); then filtersName (api filters, after
// a selection); then the raw codes. This generalises what used to be a hardcoded
// `name === 'status'` override.
export const labelFilterValue = (
  filter: Pick<TableFilter, 'name' | 'constant_name'>,
  filters: FilterValues,
  constants: Record<string, unknown>,
  filtersName: Record<string, Record<string, string>>,
): string => {
  const codes = toCodes(filters[filter.name]);
  const constantOptions = filter.constant_name ? constants[filter.constant_name] : undefined;
  if (Array.isArray(constantOptions)) return labelByConstant(codes, constantOptions);

  const names = filtersName[filter.name];
  if (names) return codes.map(code => names[code] ?? code).join(', ');

  return codes.join(', ');
};
